import React, { useEffect, useState } from 'react';
import { SbEditableContent } from 'storyblok-react';
import { blokToComponent } from '../../helpers';
import { AnyProps } from '../../types';
import { useLimit } from './hooks/use-limit';
import { useCart } from './hooks/use-cart';
import { useProducts } from './hooks/use-products';
import { CartRow } from './cart-row';
import { useInvoice } from './hooks/use-invoice';
import { getCheckoutSteps } from '../checkout/constants';
import { Link } from '../../../services/navigation/types';
import { AdobeTracking } from '../../../tracking';

interface CartProps extends AnyProps {
  blok: CartBlok;
}

interface CartBlok {
  component: string;
  empty_state: SbEditableContent[];
  has_limit: boolean;
  over_limit_state: SbEditableContent[];
  continue_shopping_url?: Link;
}

export const Cart = (props: CartProps): JSX.Element => {
  const { slot, getComponent } = props;
  const {
    empty_state: emptyStateBloks,
    has_limit: hasLimit,
    over_limit_state: overLimitBloks,
    continue_shopping_url: continueShoppingUrl,
  } = props.blok;
  const { cart, isFetchingCart, cartError } = useCart();
  const { products, isFetchingProducts } = useProducts(cart);
  const priceAndQuantities = products
    .map((product, index) => (
      {
        price: product.content?.price,
        quantity: cart[index]?.quantity || 0,
      }
    ));
  const isOverLimit = useLimit(priceAndQuantities);
  const invoice = useInvoice(priceAndQuantities);
  const [hasTracked, setHasTracked] = useState(false);
  const updatedData = {
    currency: 'EUR',
    items: products.map((product, index) => ({
      item_id: product.uuid,
      item_name: product.content?.name,
      currency: 'EUR',
      discount: '',
      item_brand: 'Roche',
      item_category: 'Product',
      item_variant: '',
      price: product.content?.price,
      quantity: cart[index]?.quantity || 0,
    })),
  };

  useEffect(() => {
    if (!isFetchingProducts && products.length > 0 && !hasTracked) {
      AdobeTracking(updatedData, 'scView');
      setHasTracked(true);
    }
  }, [isFetchingProducts, products, hasTracked]);

  if (isFetchingCart || isFetchingProducts) {
    return <roche-loading-spinner/>;
  }

  if (cartError) {
    return (
      <roche-richtext
        slot={slot}
        style={{ color: 'var(--color-status--error)' }}
      >
        <h3>We couldn&apos;t retrieve your cart&apos;s data</h3>
        <p>{cartError}</p>
      </roche-richtext>
    );
  }

  if (cart.length <= 0) {
    return (
      <div
        slot={slot}
        style={{
          textAlign: 'center',
          fill: '#0b41cd',
          padding: '0 0 6rem',
        }}
      >
        <roche-icon
          icon="cart-empty"
          height="96px"
        />
        {emptyStateBloks.map((blok) => blokToComponent({ blok, getComponent }))}
      </div>
    );
  }

  return (
    <div slot={slot}>
      <roche-journey
        current-step={0}
        steps={JSON.stringify(getCheckoutSteps(''))}
      />

      {products.map((product, index) => <CartRow key={index} {...product} {...cart[index]}/>)}
      <roche-invoice
        {...{ ...invoice, taxPercent: `${invoice.taxPercent}` }}
        sum-label="Zwischensumme"
        tax-label="USt "
        tax-percent={`(${invoice.taxPercent}%)`}
        shipping-label="Versandkosten"
        shipping-tax={invoice.shippingTax}
        total-label="Gesamtbetrag"
      />
      <roche-grid
        layout="8-4"
        full-width
        style={{ paddingTop: '0' }}
      >
        {hasLimit && isOverLimit && overLimitBloks.map((blok) => blokToComponent({ blok, getComponent, slot: 'left' }))}
        <div
          slot="right"
          style={{ textAlign: 'right' }}
        >
          {
            continueShoppingUrl?.cached_url && <roche-button-global
              appearance="secondary"
              text="Weiter einkaufen"
              href={`/${continueShoppingUrl.cached_url}`}
              no-margin-medium-up
            />
          }
          <roche-button-global
            appearance="primary"
            disabled={(hasLimit && isOverLimit) || Boolean(invoice.error)}
            text="Zur Kasse"
            href="/order-details"
            no-margin-medium-up
          />
          {invoice.error && (
            <roche-richtext>
              <p style={{ color: 'var(--color-status--error)' }}>
                <strong>{invoice.error}</strong>
              </p>
            </roche-richtext>
          )}
        </div>
      </roche-grid>
    </div>
  );
};
